import React, { useEffect, useMemo, useState } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useCategory, useSubCategory } from "@features/cx/consumerOnboarding/hooks";
import { useAreaList, useSubAreaList } from "../../hooks";
import { FIELD_CLASSES } from "@shared/utils/constants";

interface ConsumerFilters {
  category: string;
  subCategory: string;
  area: string;
  subArea: string;
}

interface ConsumerFiltersPanelProps {
  category: string;
  subCategory: string;
  area: string;
  subArea: string;
  onApplyFilters: (filters: ConsumerFilters) => void;
  onResetFilters: () => void;
}

export const ConsumerFilter: React.FC<ConsumerFiltersPanelProps> = ({
  category,
  subCategory,
  area,
  subArea,
  onApplyFilters,
  onResetFilters,
}) => {

  // --------------------------
  // Fetch initial lists
  // --------------------------
  const { data: categoryData } = useCategory();
  const { data: areaList } = useAreaList();

  // --------------------------
  // Local states for selections
  // --------------------------
  const [selectedCategory, setSelectedCategory] = useState(category);
  const [selectedArea, setSelectedArea] = useState(area);

  // --------------------------
  // Sub Category & Sub Area Hooks
  // Hooks must ALWAYS run unconditionally
  // --------------------------
  const subCategoryResult = useSubCategory(selectedCategory ? [selectedCategory] : []);

  const subAreaResult = useSubAreaList(selectedArea ? [selectedArea] : []);

  // --------------------------
  // Triggering API using useEffect
  // --------------------------
  useEffect(() => {
    if (selectedCategory) {
      subCategoryResult.refetch();
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedArea) {
      subAreaResult.refetch();
    }
  }, [selectedArea]);

  // Extract data
  const subCategoryData = subCategoryResult?.data;
  const subAreaDataList = subAreaResult?.data;

  // --------------------------
  // Transform options
  // --------------------------
  const categoryOptions = useMemo(
    () =>
      categoryData?.result?.map((cat: any) => ({
        value: cat.code,
        label: cat.name,
      })) || [],
    [categoryData]
  );

  const subCategoryOptions = useMemo(
    () =>
      subCategoryData?.result?.map((subCat: any) => ({
        value: subCat.code,
        label: subCat.name,
      })) || [],
    [subCategoryData]
  );

  const areaOptions = useMemo(
    () =>
      areaList?.map((areaItem: any) => ({
        value: areaItem.id,
        label: areaItem.name,
      })) || [],
    [areaList]
  );

  const subAreaOptions = useMemo(
    () =>
      subAreaDataList?.map((subArea: any) => ({
        value: subArea.id,
        label: subArea.name,
      })) || [],
    [subAreaDataList]
  );

  // --------------------------
  // Form fields
  // --------------------------
  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "category",
        label: "Category",
        type: "select",
        placeholder: "Select Category",
        options: categoryOptions,
      },
      {
        name: "subCategory",
        label: "Sub Category",
        type: "select",
        placeholder: "Select Sub Category",
        options: subCategoryOptions,
        disabled: !selectedCategory,
      },
      {
        name: "area",
        label: "Area",
        type: "select",
        placeholder: "Select Area",
        options: areaOptions,
      },
      {
        name: "subArea",
        label: "Sub Area",
        type: "select",
        placeholder: "Select Sub Area",
        options: subAreaOptions,
        disabled: !selectedArea,
      },
    ],
    [categoryOptions, subCategoryOptions, areaOptions, subAreaOptions, selectedCategory, selectedArea]
  );

  // --------------------------
  // Create form
  // --------------------------
  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
  });

  // Sync initial props
  useEffect(() => {
    form.reset({ category, subCategory, area, subArea });
    setSelectedCategory(category);
    setSelectedArea(area);
  }, [category, subCategory, area, subArea]);

  // --------------------------
  // Handle Change
  // --------------------------
  const handleFieldChange = (field: string, value: any) => {
    if (field === "category") {
      setSelectedCategory(value);
      form.setValue("subCategory", "");
    }
    if (field === "area") {
      setSelectedArea(value);
      form.setValue("subArea", "");
    }
  };

  // Submit
  const handleFilterSubmit = () => {
    const values = form.getValues();
    onApplyFilters({
      category: values.category || "",
      subCategory: values.subCategory || "",
      area: values.area || "",
      subArea: values.subArea || "",
    });
  };

  // Reset
  const handleFilterReset = () => {
    form.reset({ category: "", subCategory: "", area: "", subArea: "" });
    setSelectedCategory("");
    setSelectedArea("");
    onResetFilters();
  };

  return (
    <DynamicForm
      fields={filterFields}
      form={form}
      onSubmit={handleFilterSubmit}
      onReset={handleFilterReset}
      onFieldChange={handleFieldChange}
      gridColumns={4}
      config={{
        layout: "vertical",
        showSubmitButton: true,
        showResetButton: true,
        submitButtonText: "Apply Filters",
        resetButtonText: "Clear",
        classes: {
      ...FIELD_CLASSES, // keep all defaults
      buttonContainer: "flex justify-end gap-3", // override only this
    },
      }}
    />
  );
};
 